import { setErrorsToForm } from "@/utils/formUtils";
import { validatePin } from "@/utils/validationUtils";
import type { NamePath } from "@rc-component/form/es/interface";
import { FormInstance } from "antd/lib/form";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo } from "react";
import {
  useValidateContractGainers,
  useValidateContractNumbers,
  useValidateContractSigner,
} from "../../common/api/queries";
import type { UUID } from "../../typings/global";
import { ContractType } from "./enums";
import type { CreateUpdateContractGainerRecord } from "./types";

const contractGainerRecordFieldNames = [
  "gainer1Id",
  "gainer2Id",
  "gainer3Id",
  "gainer4Id",
  "gainer5Id",
] satisfies (keyof CreateUpdateContractGainerRecord)[];

const getGainerRecordsNamePaths = (gainerRecords: CreateUpdateContractGainerRecord[] = []): NamePath[] =>
  gainerRecords.flatMap((_, index) =>
    contractGainerRecordFieldNames.map<NamePath>((fieldName) => ["gainerRecords", index, fieldName]),
  );

interface ContractValidationFields {
  type?: ContractType;
  contractNumber?: string;
  secondaryContractNumber?: string;
  institutionId?: UUID;
  signDate?: string;
  signerId?: UUID;
  clientIdentifiers?: string[];
  gainerRecords?: CreateUpdateContractGainerRecord[];
}

interface UseContractValidationParams<T extends ContractValidationFields> {
  form: FormInstance<T>;
  contractId?: UUID;
}

const clearFormErrors = (form: FormInstance, fieldNames: NamePath[]) => {
  form.setFields(fieldNames.map((name) => ({ name, errors: [] })));
};

export const useContractNumbersValidation = <T extends ContractValidationFields>({
  form,
  contractId,
}: UseContractValidationParams<T>) => {
  const { mutate: validateContractNumbers } = useValidateContractNumbers();

  const debouncedValidateContractNumbers = useMemo(
    () =>
      debounce((currentContractId: UUID | undefined, currentContractType: ContractType) => {
        form
          .validateFields(["contractNumber", "secondaryContractNumber", "institutionId"], { validateOnly: true })
          .then((values) => {
            if (!values.institutionId) {
              return;
            }

            validateContractNumbers(
              {
                contractId: currentContractId,
                contractType: currentContractType,
                contractNumber: values.contractNumber,
                secondaryContractNumber: values.secondaryContractNumber,
                institutionId: values.institutionId,
              },
              {
                onError: (error) => setErrorsToForm(form, "contract.attrs", error.violations),
                onSuccess: () => clearFormErrors(form, ["contractNumber", "secondaryContractNumber", "institutionId"]),
              },
            );
          })
          .catch(() => undefined);
      }, 500),
    [form, validateContractNumbers],
  );

  const handleContractNumbersChange = useCallback(
    (changedValues: Partial<T>, values: T) => {
      const shouldValidateContractNumbers =
        "contractNumber" in changedValues ||
        "secondaryContractNumber" in changedValues ||
        "institutionId" in changedValues;

      if (!shouldValidateContractNumbers) {
        return;
      }

      if (!values.type || !values.institutionId || (!values.contractNumber && !values.secondaryContractNumber)) {
        debouncedValidateContractNumbers.cancel();
        return;
      }

      debouncedValidateContractNumbers(contractId, values.type);
    },
    [contractId, debouncedValidateContractNumbers, form],
  );

  useEffect(() => {
    return () => {
      debouncedValidateContractNumbers.cancel();
    };
  }, [debouncedValidateContractNumbers]);

  return handleContractNumbersChange;
};

export const useContractSignerValidation = <T extends ContractValidationFields>({
  form,
  contractId,
}: UseContractValidationParams<T>) => {
  const { mutate: validateContractSigner } = useValidateContractSigner();

  const debouncedValidateContractSigner = useMemo(
    () =>
      debounce((currentContractId: UUID | undefined, currentContractType: ContractType) => {
        form
          .validateFields(["signDate", "signerId"], { validateOnly: true })
          .then((values) => {
            if (!values.signDate || !values.signerId) {
              return;
            }

            validateContractSigner(
              {
                contractId: currentContractId,
                contractType: currentContractType,
                signDate: values.signDate,
                signerId: values.signerId,
              },
              {
                onError: (error) => setErrorsToForm(form, "contract.attrs", error.violations),
                onSuccess: () => clearFormErrors(form, ["signDate", "signerId"]),
              },
            );
          })
          .catch(() => undefined);
      }, 500),
    [form, validateContractSigner],
  );

  const handleContractSignerChange = useCallback(
    (changedValues: Partial<T>, values: T) => {
      const shouldValidateContractSigner = "signDate" in changedValues || "signerId" in changedValues;

      if (!shouldValidateContractSigner) {
        return;
      }

      if (!values.type || !values.signDate || !values.signerId) {
        debouncedValidateContractSigner.cancel();
        return;
      }

      debouncedValidateContractSigner(contractId, values.type);
    },
    [contractId, debouncedValidateContractSigner, form],
  );

  useEffect(() => {
    return () => {
      debouncedValidateContractSigner.cancel();
    };
  }, [debouncedValidateContractSigner]);

  return handleContractSignerChange;
};

export const useContractGainersValidation = <T extends ContractValidationFields>({
  form,
  contractId,
}: UseContractValidationParams<T>) => {
  const { mutate: validateContractGainers } = useValidateContractGainers();

  const debouncedValidateContractGainers = useMemo(
    () =>
      debounce((currentContractType: ContractType) => {
        form
          .validateFields([["clientIdentifiers", 0], "gainerRecords"], { validateOnly: true })
          .then((values) => {
            const currentPolicyHolderPin = values.clientIdentifiers?.[0];

            if (!currentPolicyHolderPin || !values.gainerRecords?.length) {
              return;
            }

            if (!validatePin(currentPolicyHolderPin)) {
              clearFormErrors(form, [...getGainerRecordsNamePaths(values.gainerRecords)]);
              return;
            }

            validateContractGainers(
              {
                contractId: contractId,
                contractType: currentContractType,
                policyHolderPin: currentPolicyHolderPin,
                gainerRecords: values.gainerRecords,
              },
              {
                onError: (error) => setErrorsToForm(form, "contract.attrs", error.violations),
                onSuccess: () =>
                  clearFormErrors(form, [["clientIdentifiers", 0], ...getGainerRecordsNamePaths(values.gainerRecords)]),
              },
            );
          })
          .catch(() => undefined);
      }, 500),
    [form, validateContractGainers, contractId],
  );

  const handleContractGainersChange = useCallback(
    (changedValues: Partial<T>, values: T) => {
      const shouldValidateContractGainers = "clientIdentifiers" in changedValues || "gainerRecords" in changedValues;

      if (!shouldValidateContractGainers) {
        return;
      }

      const policyHolderPin = values.clientIdentifiers?.[0];

      if (!values.type || !policyHolderPin || !values.gainerRecords?.length || !values.gainerRecords[0]?.startDate) {
        debouncedValidateContractGainers.cancel();
        return;
      }

      debouncedValidateContractGainers(values.type);
    },
    [debouncedValidateContractGainers, form],
  );

  useEffect(() => {
    return () => {
      debouncedValidateContractGainers.cancel();
    };
  }, [debouncedValidateContractGainers]);

  return handleContractGainersChange;
};

export const useContractValidation = <T extends ContractValidationFields>({
  form,
  contractId,
}: UseContractValidationParams<T>) => {
  const handleContractNumbersChange = useContractNumbersValidation({ form, contractId });
  const handleContractSignerChange = useContractSignerValidation({ form, contractId });
  const handleContractGainersChange = useContractGainersValidation({ form, contractId });

  return useCallback(
    (changedValues: Partial<T>, values: T) => {
      handleContractNumbersChange(changedValues, values);
      handleContractSignerChange(changedValues, values);
      handleContractGainersChange(changedValues, values);
    },
    [handleContractGainersChange, handleContractNumbersChange, handleContractSignerChange],
  );
};
